import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";
import { enums } from "./enums";
import { useReport } from "../../../../../context/report/ReportContext";
import _ from "lodash";

const hoursOpts = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    const hour = h % 12 === 0 ? 12 : h % 12;
    const minutes = m === 0 ? "00" : m;
    const ampm = h < 12 ? "AM" : "PM";
    const label = `${hour.toString().padStart(2, "0")}:${minutes} ${ampm}`;
    const value = `${h.toString().padStart(2, "0")}:${minutes}`;
    hoursOpts.push({
      label,
      value,
    });
  }
}

export const returnIsValid = (error, triedToSave) => {
  if (!triedToSave) return null;
  if (error) return false;
  return true;
};

export const useConstants = () => {
  const [t] = useTranslation("investigation");
  const { state: reportState, setInvestigationContext } = useReport();
  const theme = useTheme();

  // util: normaliza a array "formatted-like"
  const getFormattedArray = (maybe) => {
    if (!maybe) return [];
    if (Array.isArray(maybe)) return maybe;
    if (typeof maybe === "object") {
      if (Array.isArray(maybe.formatted)) return maybe.formatted;
      if (Array.isArray(maybe.raw)) return maybe.raw;
      // Intentar extraer arrays dentro del objeto
      return Object.values(maybe).flat().filter(Boolean);
    }
    return [];
  };

  // util: compara valores coercionando a string (evita problemas number vs string)
  const valueEquals = (optValue, target) => {
    try {
      return String(optValue) === String(target);
    } catch {
      return false;
    }
  };

  const [localState] = useState({
    enums,
    involveds: {
      involvedsKindInfo: {
        victim: {
          title: t("involved.victim_maybe_plural"),
          color: theme.palette.info.main,
        },
        accomplice: {
          title: t("involved.accomplice_maybe_plural"),
          color: theme.palette.blueGray600.main,
        },
        offender: {
          title: t("involved.offender_maybe_plural"),
          color: theme.palette.red.main,
        },
        witness: {
          title: t("involved.witness_maybe_plural"),
          color: theme.palette.success.main,
        },
      },
      involvedTypeOpts: [
        { value: "accomplice", label: t("involved.accomplice") },
        { value: "offender", label: t("involved.offender") },
        { value: "victim", label: t("involved.victim") },
        { value: "witness", label: t("involved.witness") },
      ],
      indicatedOpts: [
        { value: "complainant", label: t("General.complainant") },
        { value: "reviewer", label: t("General.reviewer") },
      ],
      colaboratorOpts: [
        { value: "external", label: t("involved.external") },
        { value: "internal", label: t("involved.internal") },
      ],
      statusOpts: [
        {
          value: "pending",
          label: t("involved.pending_confirmation"),
        },
        {
          value: "confirmed",
          label: t("involved.confirmed"),
        },
        {
          value: "out",
          label: t("involved.not_participated"),
        },
        {
          value: "cant_confirm",
          label: t("involved.cant_confirmed"),
        },
      ],
      genderOpts: [
        { value: "male", label: t("involved.male") },
        { value: "female", label: t("involved.female") },
        { value: "other", label: t("involved.other") },
      ],
      reinsidenceOpts: [
        { value: "yes", label: t("involved.yes") },
        { value: "no", label: t("involved.no") },
      ],
    },
    resources: {
      accessResourcesOpts: [
        { value: "access_to_cameras", label: t("resources.access_to_cameras") },
        {
          value: "access_to_documents",
          label: t("resources.access_to_documents"),
        },
        {
          value: "access_to_specific_instalations",
          label: t("resources.access_to_specific_instalations"),
        },
        {
          value: "access_to_internet",
          label: t("resources.access_to_internet"),
        },
        {
          value: "access_to_make_inventories",
          label: t("resources.access_to_make_inventories"),
        },
        {
          value: "access_to_computer_systems",
          label: t("resources.access_to_computer_systems"),
        },
        { value: "offices_usage", label: t("resources.offices_usage") },
        {
          value: "meeting_room_usage",
          label: t("resources.meeting_room_usage"),
        },
        { value: "other", label: t("resources.other") },
      ],
      expertiseOpts: [
        { value: "auditor", label: t("resources.auditor") },
        { value: "consultant", label: t("resources.consultant") },
        { value: "finance", label: t("resources.finance") },
        { value: "manager", label: t("resources.manager") },
        { value: "graphologist", label: t("resources.graphologist") },
        { value: "infiltrate", label: t("resources.infiltrate") },
        {
          value: "computer_forensics",
          label: t("resources.computer_forensics"),
        },
        { value: "investigación", label: t("resources.investigación") },
        { value: "proficient", label: t("resources.proficient") },
        { value: "polygraphist", label: t("resources.polygraphist") },
      ],
      collaboratorOpts: [
        { value: "internal", label: t("resources.internal") },
        { value: "external", label: t("resources.external") },
      ],
      roles: {
        evidence_analysis: t("resources.evidence_analysis"),
        interview_support: t("resources.interview_support"),
        research_approval: t("resources.research_approval"),
        research_advisor: t("resources.research_advisor"),
        data_entry: t("resources.data_entry"),
        evidence_custody: t("resources.evidence_custody"),
        interview_design: t("resources.interview_design"),
        interview_execution: t("resources.interview_execution"),
        evidence_facilitator: t("resources.evidence_facilitator"),
        research_report: t("resources.research_report"),
        research_plan: t("resources.research_plan"),
        research_resources: t("resources.research_resources"),
        research_manager: t("resources.research_manager"),
        interview_witness: t("resources.interview_witness"),
        translator: t("resources.translator"),
        other_research_activities: t("resources.other_research_activities"),
      },
      resourceTypeOpts: [
        { value: "access", label: t("resources.access_resource") },
        { value: "financial", label: t("resources.financial_resource") },
        { value: "human", label: t("resources.human_resource") },
        { value: "material", label: t("resources.material_resource") },
      ],
    },
    interviews: {
      typeInterviewOpts: [
        { label: t("Interview.In_person"), value: "in-person" },
        { label: t("Interview.videocall"), value: "video-conference" },
      ],
      statusOpts: [
        {
          value: "pending",
          label: t("Interview.pending"),
          color: (theme) => theme.palette.success.main,
        },
        {
          value: "process",
          label: t("Interview.progress"),
          color: (theme) => theme.palette.warning.main,
        },
        {
          value: "finish",
          label: t("Interview.completed"),
          color: (theme) => theme.palette.error.main,
        },
      ],
      rolesOpts: [
        {
          label: t("investigation.investigation_lead"),
          value: "investigation_responsable",
        },
        {
          label: t("investigation.investigation_plan"),
          value: "investigation_plan",
        },
        {
          label: t("investigation.investigation_resources"),
          value: "investigation_resources",
        },
        {
          label: t("investigation.interview_design"),
          value: "interview_design",
        },
        {
          label: t("investigation.interview_execution"),
          value: "interview_executor",
        },
        {
          label: t("investigation.interview_witness"),
          value: "interview_witness",
        },
        {
          label: t("investigation.interview_support"),
          value: "interview_help",
        },
        { label: t("investigation.translator"), value: "translator" },
        {
          label: t("investigation.data_entry_clerk"),
          value: "data_entry_operator",
        },
        {
          label: t("investigation.evidence_facilitator"),
          value: "evidence_facilitator",
        },
        {
          label: t("investigation.evidence_custody"),
          value: "evidence_custody",
        },
        {
          label: t("investigation.evidence_analysis"),
          value: "evidence_analysis",
        },
        {
          label: t("investigation.investigation_advisor"),
          value: "investigation_advisor",
        },
        {
          label: t("investigation.investigation_report"),
          value: "investigation_report",
        },
        {
          label: t("investigation.investigation_approval"),
          value: "investigation_approve",
        },
        {
          label: t("investigation.other_investigation_activities"),
          value: "investigation_other",
        },
      ],
      typeFileOpts: [
        {
          value: "signed_questions_and_answers",
          label: t("Interview.signed_questions_and_answers"),
        },
        {
          value: "interview_consent",
          label: t("Interview.signed_interview_consent"),
        },
        {
          value: "recording_consent",
          label: t("Interview.signed_consent_interview_recording"),
        },
        {
          value: "interview_recording",
          label: t("Interview.interview_recording"),
        },
        {
          value: "interview_minute",
          label: t("Interview.interview_minute"),
        },
        {
          value: "other",
          label: t("Interview.other"),
        },
      ],

      pointsToEvaluateLabels: {
        directWitness: t("Interview.is_the_person_an_eyewitness_to_the_events"),
        offenderHistory: t(
          "Interview.does_offender_accomplice_have_history_similar_conduct"
        ),
        intervieweeReputation: t(
          "Interview.is_there_information_available_regarding_interviewees_reputation"
        ),
        statementsCoherent: t(
          "Interview.do_interviewees_statements_appear_reasonable"
        ),
        supportsInformant: t(
          "Interview.do_interviewees_statements_support_facts_reported_by_informant?"
        ),
        cooperativeAttitude: t(
          "Interview.did_interviewee_appear_cooperative_with_interviewer"
        ),
        lyingMotive: t("Interview.does_interviewee_have_any_motive_to_lie"),
        evidenceSupport: t(
          "Interview.is_there_evidence_that_corroborates_interviewees_statements"
        ),
        objectivesMet: t("Interview.were_objectives_interview_achieved"),
        otherInvolved: t(
          "Interview.were_other_potential_individuals_involved_case_identified"
        ),
      },
      options: [
        { label: t("evaluation.yes"), value: "yes" },
        { label: t("No"), value: "no" },
        { label: t("Custody.undetermined"), value: "not-defined" },
      ],

      connectionProviderOpts: [
        { value: "zoom", label: t("Zoom") },
        { value: "google_meet", label: t("Google Meet") },
        { value: "teams", label: t("Teams") },
        { value: "discord", label: t("Discord") },
        { value: "other", label: t("Evidences.other") },
      ],
    },
    evaluation: {
      priorityOpts: [
        { value: "low", label: t("evaluation.low") },
        { value: "medium", label: t("evaluation.medium") },
        { value: "hight", label: t("evaluation.high") },
      ],
    },
    plans: {
      statusOpts: [
        {
          label: t("activityPlan.pending"),
          value: "pending",
        },
        {
          label: t("activityPlan.in_process"),
          value: "process",
        },
        {
          label: t("activityPlan.finished"),
          value: "completed",
        },
      ],
      statusTodosOpts: [
        {
          label: t("activityPlan.pending"),
          value: false,
        },
        {
          label: t("activityPlan.completed"),
          value: true,
        },
      ],
    },
    evidences: {
      kindOpts: [
        {
          label: t("Evidences.physical"),
          value: "fisic",
        },
        {
          label: t("Evidences.digital"),
          value: "digital",
        },
      ],
      typeOpts: [
        {
          label: t("Evidences.audio"),
          value: "audio",
        },
        {
          label: t("Evidences.video"),
          value: "video",
        },
        {
          label: t("Evidences.image"),
          value: "image",
        },
        {
          label: t("Evidences.document"),
          value: "document",
        },
        {
          label: t("Evidences.other"),
          value: "other",
        },
      ],
      fundamentaOpts: [
        {
          label: t("Evidences.yes"),
          value: "yes",
        },
        {
          label: t("Evidences.no"),
          value: "no",
        },
        {
          label: t("Evidences.partially"),
          value: "partially",
        },
      ],
      kindMoveCustodyOpts: [
        {
          label: t("Evidences.analysis_on_site"),
          value: "income",
        },
        {
          label: t("Evidences.initial_collection"),
          value: "collection",
        },
        {
          label: t("Evidences.transfer"),
          value: "move",
        },
      ],
    },
    selects: {
      hoursOpts,
      typeUserLabels: {
        [enums.typeUser.ME]: t("type_users.ME"),
        [enums.typeUser.INVOLVEDS]: t("type_users.INVOLVEDS"),
        [enums.typeUser.INVOLVEDS_VICTIM]: t("type_users.INVOLVEDS_VICTIM"),
        [enums.typeUser.INVOLVEDS_ACCOMPLICE]: t(
          "type_users.INVOLVEDS_ACCOMPLICE"
        ),
        [enums.typeUser.INVOLVEDS_OFFENDER]: t("type_users.INVOLVEDS_OFFENDER"),
        [enums.typeUser.INVOLVEDS_WITNESS]: t("type_users.INVOLVEDS_WITNESS"),
        [enums.typeUser.RESOURCES]: t("type_users.RESOURCES"),
        [enums.typeUser.EXTERNAL]: t("type_users.EXTERNAL"),
        [enums.typeUser.SYSTEM]: t("type_users.SYSTEM"),
      },
    },
    investigation: {
      allowOnSimple: [
        enums.sections.researchFolder,
        enums.sections.caseEvaluation,
        enums.sections.involveds,
        enums.sections.evidences,
        enums.sections.conclusion,
      ],
    },
  });

  const functions = {
    askUpdateInvestigationContext: (investigation) => {
      try {
        setInvestigationContext(investigation);
      } catch (error) {
        console.error("Error updating investigation in context", error);
      }
    },

    // --- versión robusta de getUserLabel ---
    getUserLabel: (user) => {
      if (!user) return null;
      if (typeof user === "string") return user;

      const { id, from, label } = user;

      // Snapshot del label (nombre real al momento de guardar). Lo preferimos
      // salvo que sea una cadena legacy genérica de "me" (donde antes se
      // guardaba el texto del CTA en vez del username).
      if (label) {
        const legacyMeLabels = new Set([
          t("activityPlan.assign_to_me"),
          t("activityPlan.participate_in_the_activity"),
          t("Evidences.assing_evidence_to_me"),
          t("type_users.ME"),
          "Asignarme la entrevista",
          "Participar en la entrevista",
        ]);
        if (!legacyMeLabels.has(label)) return label;
      }

      // session email (misma lógica que usabas)
      const sessionEmail = JSON.parse(localStorage.getItem("Session"))?.email;
      if (sessionEmail === id)
        return `${sessionEmail} (${t("Interview.myself")})`;
      if (from === enums.typeUser.COMPLAINANT) return t("Interview.complainant");

      // 1) Buscar en users y me (opciones formateadas)
      const usersFormatted = getFormattedArray(
        reportState?.currentReport?.users
      );
      const meFormatted = getFormattedArray(reportState?.currentReport?.me);
      const combinedBase = [...usersFormatted, ...meFormatted];

      let found = combinedBase.find((opt) => valueEquals(opt?.value, id));
      if (found) return found.label;

      // 2) Si 'from' está definido, buscar en la ruta correspondiente (involveds / recursos, etc.)
      const routePath = enums.typeUserRoutes?.[from];
      if (routePath) {
        const routeArray = getFormattedArray(_.get(reportState, routePath));
        found = routeArray.find((opt) => valueEquals(opt?.value, id));
        if (found) return found.label;
        // Si el value en routeArray tiene formato compuesto (ej. "name###expertise" o "name-role"),
        // intentar búsqueda por includes (coincidencia parcial)
        found = routeArray.find((opt) =>
          String(opt?.value).includes(String(id))
        );
        if (found) return found.label;
      }

      // 3) fallback: intentar búsqueda por label que contenga id
      const combinedAll = [
        ...combinedBase,
        ...(routePath ? getFormattedArray(_.get(reportState, routePath)) : []),
      ];
      const foundByLabel = combinedAll.find((opt) =>
        String(opt?.label || "")
          .toLowerCase()
          .includes(String(id).toLowerCase())
      );
      if (foundByLabel) return foundByLabel.label;

      // Fallback: if id is an email, show it instead of "not found"
      // (covers participants added by email that aren't in the registered users list)
      if (typeof id === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(id)) {
        return id;
      }

      return t("Usuario no encontrado");
    },

    // New: Formal format
    getUsersWitness: (user) => {
      if (!user) return null;
      if (typeof user === "string") return user;

      const { id, from } = user;

      // session email (misma lógica que usabas)
      const sessionEmail = JSON.parse(localStorage.getItem("Session"))?.email;
      if (sessionEmail === id) return `${sessionEmail}`;

      // 1) Buscar en users y me (opciones formateadas)
      const usersFormatted = getFormattedArray(
        reportState?.currentReport?.users
      );

      const meFormatted = getFormattedArray(reportState?.currentReport?.me);
      const combinedBase = [...usersFormatted, ...meFormatted];

      let found = combinedBase.find((opt) => valueEquals(opt?.value, id));
      if (found) return found.label;

      // 2) Si 'from' está definido, buscar en la ruta correspondiente (involveds / recursos, etc.)
      const routePath = enums.typeUserRoutes?.[from];
      if (routePath) {
        const routeArray = getFormattedArray(_.get(reportState, routePath));
        found = routeArray.find((opt) => valueEquals(opt?.value, id));
        if (found) return found.label;
        found = routeArray.find((opt) =>
          String(opt?.value).includes(String(id))
        );
        if (found) return found.label;
      }

      // 3) fallback: intentar búsqueda por label que contenga id
      const combinedAll = [
        ...combinedBase,
        ...(routePath ? getFormattedArray(_.get(reportState, routePath)) : []),
      ];
      const foundByLabel = combinedAll.find((opt) =>
        String(opt?.label || "")
          .toLowerCase()
          .includes(String(id).toLowerCase())
      );
      if (foundByLabel) return foundByLabel.label;

      // Fallback: use email as the visible name when the participant
      // is not in the registered users list but has an email-shaped id.
      if (typeof id === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(id)) {
        return id;
      }

      return "";
    },

    getResourceLabel: (resource) => {
      if (!resource || !resource.type) return t("Recurso no encontrado");

      const inv = reportState?.currentReport?.investigation ?? {};

      const human = getFormattedArray(
        inv?.humanResourcesInfo ?? inv?.humanResourcesInfo?.formatted
      );
      const access = getFormattedArray(
        inv?.accessResourcesInfo ?? inv?.accessResourcesInfo?.formatted
      );
      const financial = getFormattedArray(
        inv?.financialResourcesInfo ?? inv?.financialResourcesInfo?.formatted
      );
      const material = getFormattedArray(
        inv?.materialResourcesInfo ?? inv?.materialResourcesInfo?.formatted
      );

      let resources = [];
      switch (resource.type) {
        case enums.typeResource.ACCESS:
          resources = access;
          break;
        case enums.typeResource.HUMAN:
          resources = human;
          break;
        case enums.typeResource.FINANCIAL:
          resources = financial;
          break;
        case enums.typeResource.MATERIAL:
          resources = material;
          break;
        default:
          resources = [];
      }

      if (!resources.length) return t("Recurso no encontrado");

      let found = resources.find((opt) =>
        valueEquals(opt?.value, resource.resource)
      );
      if (found) return found.label;

      if (resource.resource && typeof resource.resource === "string") {
        found = resources.find((opt) =>
          String(opt?.value).includes(String(resource.resource))
        );
        if (found) return found.label;
      }

      if (resource.type === enums.typeResource.ACCESS) {
        const resourceLabelFromList = resources.find((opt) =>
          valueEquals(opt?.value, resource.resource)
        )?.label;
        const mapped = localState.resources.accessResourcesOpts.find(
          (opt) =>
            opt.value === resourceLabelFromList ||
            opt.label === resourceLabelFromList
        );
        if (mapped) return mapped.label;
      }

      return t("Recurso no encontrado");
    },
  };

  return { ...localState, functions };
};
